import type { FetchArgType } from "openapi-typescript-fetch";
import type {
    GetListParams,
    GetListResult,
    GetManyReferenceParams,
    GetManyReferenceResult,
    UpdateParams,
    UpdateManyParams,
    CreateParams,
    DeleteManyParams,
    DeleteManyResult,
    UpdateResult,
    GetManyResult,
    CreateResult
} from "react-admin";
import { isEmpty } from "lodash";

import { fetcher } from "../../data";
import { isDefined } from "../../utils/typedLoDashUtils";
import { assertIsDefined } from "../../utils/assertions";
import type { BaseFilters } from "../../data/BaseFilters";
import { NotImplementedError } from "../../utils/NotImplementedError";
import type { ErrorDetails, LangParams, MpmDataProvider, MpmDataProviderOperationName } from "../../data";

import type { StaticPageDTO } from "./StaticPageDTO";
import type { StaticPageRecord } from "./StaticPageRecord";
import { convertIdFilter } from "../../utils/filterUtils";

const findAll = fetcher.path("/api/edito/static-page/findAll").method("post").create();
const getOne = fetcher.path("/api/edito/static-page/{uuid}").method("get").create();
const updateOne = fetcher.path("/api/edito/static-page/{uuid}").method("put").create();
const createOne = fetcher.path("/api/edito/static-page/").method("post").create();
const deleteOne = fetcher.path("/api/edito/static-page/{uuid}").method("delete").create();
const getAllByUuids = fetcher.path("/api/edito/static-page/allByUuids").method("get").create();

/**
 * Parses the DTO from backend to record for frontend
 * @param dto
 * @returns StaticPageRecord
 */

function mapDTOToRecord(dto: StaticPageDTO): StaticPageRecord {
    // DTO from backend
    const { id = 0, uuid = "", code = "", title = "", description = "", content = "" } = dto;

    // Record for front-end
    return {
        shortId: id,
        id: uuid,
        code,
        title,
        description,
        content
    };
}

/**
 * Parses the record from frontend to DTO for backend
 * @param record
 * @returns ContentDTO
 */

function mapRecordToDTO(record: StaticPageRecord): StaticPageDTO {
    // Record from front-end
    const { shortId = 0, id = "", code = "", title = "", description = "", content = "" } = record;

    // DTO for back-end
    return {
        id: shortId,
        code,
        title,
        uuid: id as string,
        description,
        content
    };
}

interface StaticPageFilters extends BaseFilters {
    q?: string;
    id: string;
}

async function getList(params: Readonly<GetListParams>): Promise<GetListResult<StaticPageRecord>> {
    const { q: searchTerms, ...filters } = params.filter as StaticPageFilters;

    let parameters: FetchArgType<typeof findAll> = {
        filter: convertIdFilter(filters),
        pageable: {
            page: params.pagination.page - 1,
            size: params.pagination.perPage,
            sort: isEmpty(params.sort.field) ? [] : [`${params.sort.field},${params.sort.order}`]
        }
    } as const;

    if (isDefined(searchTerms)) {
        parameters = {
            ...parameters,
            filter: { ...parameters.filter, searchTerms }
        };
    }

    const { data } = await findAll(parameters);
    const dataPage = data.content;
    assertIsDefined(dataPage);
    const total = dataPage.totalElements;
    assertIsDefined(total);
    assertIsDefined(dataPage.content);

    return {
        data: dataPage.content.map((content) => mapDTOToRecord({ id: content.id ?? 0, ...content })),
        total
    };
}

async function _getOne(params: LangParams | (number | string)): Promise<StaticPageRecord> {
    const {
        data: { content: staticPageDTO }
    } = await getOne({
        uuid: params as string
    });
    assertIsDefined(staticPageDTO);

    return mapDTOToRecord({ id: staticPageDTO.id ?? 0, ...staticPageDTO });
}

async function getMany(uuids: readonly (number | string)[]): Promise<GetManyResult<StaticPageRecord>> {
    const {
        data: { content: staticPageDTO }
    } = await getAllByUuids({
        uuids: uuids as string[]
    });
    assertIsDefined(staticPageDTO);

    return { data: await Promise.all(staticPageDTO.map((dto) => mapDTOToRecord({ id: dto.id ?? 0, ...dto }))) };
}

async function getManyReference(_params: Readonly<GetManyReferenceParams>): Promise<GetManyReferenceResult<StaticPageRecord>> {
    return Promise.reject(new NotImplementedError("getManyReference"));
}

async function update(params: Readonly<UpdateParams<StaticPageRecord>>): Promise<UpdateResult<StaticPageRecord>> {
    const payload = mapRecordToDTO(params.data as StaticPageRecord);

    const parameters = { uuid: payload.uuid ?? "", ...payload };

    const { data } = await updateOne(parameters);
    const staticPageDTO = data.content;
    assertIsDefined(staticPageDTO);

    return { data: mapDTOToRecord({ id: parameters.id, ...staticPageDTO }) };
}

async function updateMany(_params: Readonly<UpdateManyParams>): Promise<string[]> {
    return Promise.reject(new NotImplementedError("updateMany"));
}

async function create(params: Readonly<CreateParams>): Promise<CreateResult<StaticPageRecord>> {
    const payload = mapRecordToDTO(params.data as any);

    assertIsDefined(payload.title);
    const parameters = { ...payload };
    delete (parameters as any)["id"];
    delete (parameters as any)["uuid"];
    const { data } = await createOne(parameters);
    const staticPageDTO = data.content;

    assertIsDefined(staticPageDTO);

    return { data: mapDTOToRecord({ id: staticPageDTO.id as number, ...staticPageDTO }) };
}

// eslint-disable-next-line no-underscore-dangle
async function _delete(uuid: number | string): Promise<StaticPageRecord> {
    const {
        data: { content: isDeleted }
    } = await deleteOne({
        uuid: uuid as string
    });
    assertIsDefined(isDeleted);

    return {} as StaticPageRecord;
}

async function deleteMany(params: Readonly<DeleteManyParams>): Promise<DeleteManyResult> {
    await Promise.all(params.ids.map((id: string) => _delete(id)));
    return {
        data: params.ids.map((id: string) => {
            id;
        })
    };
}

function getErrorDetails(_error: unknown, _operation: MpmDataProviderOperationName<StaticPageRecord>): ErrorDetails | undefined {
    // eslint-disable-next-line no-undefined
    return undefined;
}

export const StaticPageDataProvider: MpmDataProvider<StaticPageRecord> = {
    create,
    delete: _delete,
    deleteMany,
    getErrorDetails,
    getList,
    getMany,
    getManyReference,
    getOne: _getOne,
    update,
    updateMany
};
